import { useEffect, useRef, useState } from 'react'

import AirportField from './AirportField.jsx'

const emptyMember = () => ({ name: '', passports: [], home_iata: '', held_credentials: [] })

export { emptyMember }

export default function TripForm({
  meta,
  trip,
  onChange,
  onSubmit,
  busy,
  refineSignal,
  // Phase 2c. Only ever true when a PARSE produced a single traveller holding
  // several passports — the deterministic pass cannot tell two people apart,
  // so it always returns one. Never set by hand-editing the form, so a user
  // who adds a second passport to themselves is never asked.
  splitAsk = false,
  onKeepOnePerson,
  onSplitTravellers
}) {
  // The optional refine section. A results-screen nudge (refineSignal bump)
  // opens it and focuses the first departure-airport input.
  const [refineOpen, setRefineOpen] = useState(false)
  const originRef = useRef(null)
  useEffect(() => {
    if (!refineSignal) return
    setRefineOpen(true)
    const t = setTimeout(() => originRef.current?.querySelector('input')?.focus(), 50)
    return () => clearTimeout(t)
  }, [refineSignal])

  const limits = meta?.limits || {
    max_members: 12,
    max_passports: 4,
    max_permits: 6,
    max_days: 90
  }

  const setMember = (idx, patch) => {
    const members = trip.members.map((m, i) => (i === idx ? { ...m, ...patch } : m))
    onChange({ ...trip, members })
  }

  const addMember = () =>
    onChange({ ...trip, members: [...trip.members, emptyMember()] })

  const removeMember = (idx) =>
    onChange({ ...trip, members: trip.members.filter((_, i) => i !== idx) })

  const addPassport = (idx, code) => {
    const member = trip.members[idx]
    if (!code || member.passports.includes(code)) return
    setMember(idx, { passports: [...member.passports, code] })
  }

  const removePassport = (idx, code) =>
    setMember(idx, { passports: trip.members[idx].passports.filter((p) => p !== code) })

  // A held credential is { country, type }. New entries default to 'visa'; the
  // per-entry toggle switches to 'residence'. One entry per country.
  const addPermit = (idx, code) => {
    const member = trip.members[idx]
    if (!code || member.held_credentials.some((c) => c.country === code)) return
    setMember(idx, {
      held_credentials: [...member.held_credentials, { country: code, type: 'visa' }]
    })
  }

  const removePermit = (idx, code) =>
    setMember(idx, {
      held_credentials: trip.members[idx].held_credentials.filter((c) => c.country !== code)
    })

  const setPermitType = (idx, code, type) =>
    setMember(idx, {
      held_credentials: trip.members[idx].held_credentials.map((c) =>
        c.country === code ? { ...c, type } : c
      )
    })

  // Residence permits only: permanent vs temporary. Unspecified/temporary is
  // the safe default — some destinations (e.g. Mexico) require a PERMANENT
  // permit and refuse or deport holders of a temporary one, so this must
  // never silently default to permanent.
  const setPermitPermanence = (idx, code, permanent) =>
    setMember(idx, {
      held_credentials: trip.members[idx].held_credentials.map((c) =>
        c.country === code ? { ...c, permanent } : c
      )
    })

  // Budget, days and rooms are all optional; when typed they must be valid.
  const blank = (v) => v == null || v === ''
  const ready =
    trip.members.length > 0 &&
    trip.members.every((m) => m.name.trim() && m.passports.length > 0) &&
    (blank(trip.budget_per_person_usd) || Number(trip.budget_per_person_usd) > 0) &&
    (blank(trip.days) || Number(trip.days) >= 1)

  const passportName = (code) =>
    meta?.passports?.find((p) => p.code === code)?.name || code

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault()
        if (ready && !busy) onSubmit()
      }}
    >
      {/* 1 column on mobile (unchanged), 2 on desktop. Not 3-per-row by default:
          a 4-traveller group would render 3+1, an orphaned card in its own row.
          CSS Grid's default align-items:stretch equalizes each card's height to
          the tallest card IN ITS OWN ROW (not globally), so passport/permit chip
          wrapping never leaves ragged card bottoms. min-w-0 guards against the
          browser default that lets <fieldset> refuse to shrink inside a grid
          track.

          A SOLO traveller skips the grid template entirely and gets a centered,
          column-width-constrained wrapper instead (roughly what one column would
          measure in the 2-up grid, ~500px) — with grid-cols-2 still in effect a
          lone card would sit pinned to the left edge with a huge empty gap to its
          right, and a name input stretched across the full ~1024px form
          container looks broken. max-w-lg only binds once the viewport exceeds
          it, so mobile (always narrower) is unaffected either way — no separate
          breakpoint handling needed. */}
      <div
        className={
          trip.members.length === 1
            ? 'mx-auto max-w-lg'
            : 'grid grid-cols-1 gap-4 md:grid-cols-2'
        }
      >
      {trip.members.map((member, idx) => (
        <fieldset key={idx} className="min-w-0 rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <legend className="float-left text-sm font-semibold text-slate-700">
              Traveller {idx + 1}
            </legend>
            {trip.members.length > 1 && (
              <button
                type="button"
                onClick={() => removeMember(idx)}
                className="text-xs text-slate-400 underline"
              >
                remove
              </button>
            )}
          </div>

          <label className="mt-3 block text-xs font-medium text-slate-500">
            Name
            <input
              type="text"
              value={member.name}
              maxLength={60}
              onChange={(e) => setMember(idx, { name: e.target.value })}
              placeholder="e.g. Anna"
              className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2.5 text-base"
            />
          </label>

          <label className="mt-3 block text-xs font-medium text-slate-500">
            Passports
            <select
              value=""
              disabled={member.passports.length >= limits.max_passports}
              onChange={(e) => addPassport(idx, e.target.value)}
              className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-base"
            >
              <option value="">
                {member.passports.length ? 'Add another passport…' : 'Add a passport…'}
              </option>
              {(meta?.passports || []).map((p) => (
                <option key={p.code} value={p.code} disabled={member.passports.includes(p.code)}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          {member.passports.length > 0 && (
            <ul className="mt-2 flex flex-wrap gap-1.5">
              {member.passports.map((code) => (
                <li
                  key={code}
                  className="flex items-center gap-1 rounded-full bg-sky-100 px-2.5 py-1 text-xs text-sky-800"
                >
                  {passportName(code)}
                  <button
                    type="button"
                    aria-label={`remove ${code} passport`}
                    onClick={() => removePassport(idx, code)}
                    className="font-semibold text-sky-400"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* One person with two passports takes the best of the two; two
              people take the intersection. Different answer, so ask. */}
          {splitAsk && trip.members.length === 1 && member.passports.length >= 2 && (
            <div className="mt-3 rounded-lg border-l-4 border-horizon bg-horizon/10 px-3 py-2.5">
              <p className="text-xs font-medium text-slate-700">
                One person with both passports, or two travellers?
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={onKeepOnePerson}
                  className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 active:bg-slate-50"
                >
                  One person
                </button>
                <button
                  type="button"
                  onClick={onSplitTravellers}
                  className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 active:bg-slate-50"
                >
                  Two travellers
                </button>
              </div>
            </div>
          )}

          <label className="mt-3 block text-xs font-medium text-slate-500">
            Visas &amp; permits held (optional)
            <select
              value=""
              disabled={member.held_credentials.length >= limits.max_permits}
              onChange={(e) => addPermit(idx, e.target.value)}
              className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-base"
            >
              <option value="">Add a visa or residence permit…</option>
              {(meta?.passports || []).map((p) => (
                <option
                  key={p.code}
                  value={p.code}
                  disabled={member.held_credentials.some((c) => c.country === p.code)}
                >
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <p className="mt-1 text-xs text-slate-400">
            Add any long-stay visas or residence permits, including the country you live in.
            Affects where you can go without extra paperwork.
          </p>
          {member.held_credentials.length > 0 && (
            <ul className="mt-2 space-y-2">
              {member.held_credentials.map((c) => (
                <li
                  key={c.country}
                  className="flex flex-wrap items-center gap-2 rounded-lg bg-sky-50 px-2.5 py-2 text-xs text-sky-900"
                >
                  <span className="font-medium">{passportName(c.country)}</span>
                  <div
                    role="group"
                    aria-label={`${passportName(c.country)} credential type`}
                    className="inline-flex overflow-hidden rounded-md border border-sky-200"
                  >
                    {[
                      ['visa', 'Visa'],
                      ['residence', 'Residence permit']
                    ].map(([value, text]) => (
                      <button
                        key={value}
                        type="button"
                        aria-pressed={c.type === value}
                        onClick={() => setPermitType(idx, c.country, value)}
                        className={`px-2 py-1 text-[11px] font-medium ${
                          c.type === value ? 'bg-ocean text-white' : 'bg-white text-sky-700'
                        }`}
                      >
                        {text}
                      </button>
                    ))}
                  </div>
                  {c.type === 'residence' && (
                    <div
                      role="group"
                      aria-label={`${passportName(c.country)} permit duration`}
                      className="inline-flex overflow-hidden rounded-md border border-sky-200"
                    >
                      {[
                        [false, 'Temporary'],
                        [true, 'Permanent']
                      ].map(([value, text]) => (
                        <button
                          key={String(value)}
                          type="button"
                          aria-pressed={Boolean(c.permanent) === value}
                          onClick={() => setPermitPermanence(idx, c.country, value)}
                          className={`px-2 py-1 text-[11px] font-medium ${
                            Boolean(c.permanent) === value
                              ? 'bg-ocean text-white'
                              : 'bg-white text-sky-700'
                          }`}
                        >
                          {text}
                        </button>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    aria-label={`remove ${c.country} credential`}
                    onClick={() => removePermit(idx, c.country)}
                    className="ml-auto font-semibold text-sky-400"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}

        </fieldset>
      ))}
      </div>

      {trip.members.length < limits.max_members && (
        <button
          type="button"
          onClick={addMember}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-horizon py-4 text-base font-bold text-ocean shadow-sm active:opacity-90"
        >
          <span aria-hidden="true" className="text-xl font-bold leading-none">
            +
          </span>
          Add another traveller
        </button>
      )}

      <details
        open={refineOpen}
        onToggle={(e) => setRefineOpen(e.currentTarget.open)}
        className="rounded-xl border border-slate-200"
      >
        <summary className="cursor-pointer select-none px-4 py-3 text-sm font-semibold text-slate-700 [&::-webkit-details-marker]:hidden">
          Budget, days &amp; departure (optional)
        </summary>
        <div className="space-y-4 px-4 pb-4">
          <div ref={originRef}>
            <p className="text-xs font-medium text-slate-500">
              Flying from (optional — without it, flight costs use an average estimate)
            </p>
            {trip.members.map((member, idx) => (
              <label key={idx} className="mt-2 block text-xs font-medium text-slate-500">
                {trip.members.length > 1 &&
                  (member.name.trim() || `Traveller ${idx + 1}`)}
                <AirportField
                  airports={meta?.airports || []}
                  value={member.home_iata}
                  onSelect={(iata) => setMember(idx, { home_iata: iata })}
                />
              </label>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs font-medium text-slate-500">
              Budget per person (USD)
              <input
                type="number"
                min="1"
                inputMode="numeric"
                value={trip.budget_per_person_usd ?? ''}
                onChange={(e) =>
                  onChange({ ...trip, budget_per_person_usd: e.target.valueAsNumber || '' })
                }
                className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2.5 text-base"
              />
            </label>
            <label className="block text-xs font-medium text-slate-500">
              Days
              <input
                type="number"
                min="1"
                max={limits.max_days}
                inputMode="numeric"
                value={trip.days ?? ''}
                onChange={(e) => onChange({ ...trip, days: e.target.valueAsNumber || '' })}
                placeholder="7"
                className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2.5 text-base"
              />
            </label>
          </div>
          <label className="block text-xs font-medium text-slate-500">
            How many rooms?
            <input
              type="number"
              min="1"
              max={limits.max_rooms || 12}
              inputMode="numeric"
              value={trip.rooms ?? ''}
              onChange={(e) => onChange({ ...trip, rooms: e.target.valueAsNumber || '' })}
              placeholder="auto (1 per 2 travellers)"
              className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2.5 text-base"
            />
            <span className="mt-1 block text-[11px] font-normal text-slate-400">
              Lodging is split across travellers. Default: 1 room per 2 travellers.
            </span>
          </label>
        </div>
      </details>

      <button
        type="submit"
        disabled={!ready || busy}
        className="w-full rounded-xl bg-slate-900 py-3.5 text-base font-semibold text-white disabled:opacity-40"
      >
        Where can we all go?
      </button>
    </form>
  )
}
